import { useEffect, useMemo, useRef, useState } from 'react';

import { formatHz, normalizeAnalysis } from './analysis.js';
import { wasmSpectrogram } from './sound-api.js';
import { Playhead, useElementSize, useTimeView, useViewGestures, type AudioViewProps } from './view-hooks.js';
import './sound-editors.css';

/** STFT result: `cols` frames × `bins` magnitudes (0..255 = -100..0 dBFS), plus a centroid per frame (Hz). */
export interface Stft {
  cols: number;
  bins: number;
  hop: number;
  fft: number;
  rate: number;
  data: Uint8Array;
  centroid: Float32Array;
}

/**
 * Worker source (inline, so no bundler worker config is needed): Hann
 * window, radix-2 FFT, dB magnitudes and spectral centroid per frame.
 */
const WORKER_SRC = `
self.onmessage = (e) => {
  const { id, samples, rate, fft, hop } = e.data;
  const n = samples.length;
  const cols = Math.max(1, Math.floor(Math.max(0, n - 1) / hop) + 1);
  const bins = fft / 2;
  const data = new Uint8Array(cols * bins);
  const centroid = new Float32Array(cols);
  const win = new Float32Array(fft);
  let wsum = 0;
  for (let i = 0; i < fft; i++) { win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (fft - 1)); wsum += win[i]; }
  const re = new Float64Array(fft), im = new Float64Array(fft);
  const levels = Math.log2(fft);
  const rev = new Uint32Array(fft);
  for (let i = 0; i < fft; i++) { let r = 0; for (let b = 0; b < levels; b++) r |= ((i >> b) & 1) << (levels - 1 - b); rev[i] = r; }
  const cosT = new Float64Array(fft / 2), sinT = new Float64Array(fft / 2);
  for (let i = 0; i < fft / 2; i++) { cosT[i] = Math.cos((2 * Math.PI * i) / fft); sinT[i] = -Math.sin((2 * Math.PI * i) / fft); }
  const norm = 2 / wsum;
  for (let c = 0; c < cols; c++) {
    const start = c * hop - fft / 2;
    for (let i = 0; i < fft; i++) { const s = start + i; re[rev[i]] = s >= 0 && s < n ? samples[s] * win[i] : 0; im[rev[i]] = 0; }
    for (let size = 2; size <= fft; size <<= 1) {
      const half = size >> 1, step = fft / size;
      for (let i = 0; i < fft; i += size) {
        for (let j = 0, k = 0; j < half; j++, k += step) {
          const a = i + j, b = a + half;
          const tr = re[b] * cosT[k] - im[b] * sinT[k];
          const ti = re[b] * sinT[k] + im[b] * cosT[k];
          re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
        }
      }
    }
    let num = 0, den = 0;
    const row = c * bins;
    for (let k = 0; k < bins; k++) {
      const mag = Math.hypot(re[k], im[k]) * norm;
      num += mag * k; den += mag;
      const db = 20 * Math.log10(mag + 1e-10);
      data[row + k] = Math.max(0, Math.min(255, Math.round(((db + 100) / 100) * 255)));
    }
    centroid[c] = den > 1e-9 ? (num / den) * (rate / fft) : 0;
  }
  self.postMessage({ id, cols, bins, hop, fft, rate, data, centroid }, [data.buffer, centroid.buffer]);
};
`;

let worker: Worker | null = null;
let workerFailed = false;
let nextId = 1;
const pending = new Map<number, (s: Stft | null) => void>();

function getWorker(): Worker | null {
  if (worker || workerFailed) return worker;
  try {
    const url = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' }));
    worker = new Worker(url);
    worker.onmessage = (e: MessageEvent<Stft & { id: number }>) => {
      const cb = pending.get(e.data.id);
      pending.delete(e.data.id);
      cb?.(e.data);
    };
    worker.onerror = () => {
      workerFailed = true;
      for (const cb of pending.values()) cb(null);
      pending.clear();
      worker?.terminate();
      worker = null;
    };
  } catch {
    workerFailed = true;
  }
  return worker;
}

/** Computes an STFT off the main thread (null when workers are unavailable). Frame count is capped at ~8000. */
export function computeStft(left: Float32Array, right: Float32Array | null | undefined, rate: number, fft = 2048): Promise<Stft | null> {
  const w = getWorker();
  if (!w) return Promise.resolve(null);
  const n = left.length;
  const mono = new Float32Array(n);
  if (right && right.length === n) for (let i = 0; i < n; i++) mono[i] = (left[i]! + right[i]!) * 0.5;
  else mono.set(left);
  const hop = Math.max(128, Math.ceil(n / 8000));
  const id = nextId++;
  return new Promise((resolve) => {
    pending.set(id, resolve);
    w.postMessage({ id, samples: mono, rate, fft, hop }, [mono.buffer]);
  });
}

/** 256-entry magma-like colour map (RGB). */
const PALETTE: Uint8Array = (() => {
  const stops: [number, number, number, number][] = [
    [0, 0, 0, 4],
    [0.2, 40, 11, 84],
    [0.4, 120, 28, 109],
    [0.6, 205, 64, 82],
    [0.8, 251, 145, 49],
    [1, 252, 253, 191],
  ];
  const out = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const f = i / 255;
    let s = 0;
    while (s < stops.length - 2 && f > stops[s + 1]![0]) s++;
    const a = stops[s]!;
    const b = stops[s + 1]!;
    const t = (f - a[0]) / (b[0] - a[0]);
    for (let c = 0; c < 3; c++) out[i * 3 + c] = Math.round(a[c + 1]! + (b[c + 1]! - a[c + 1]!) * t);
  }
  return out;
})();

const F_MIN = 20;

/**
 * Spectrogram of a rendered buffer (log frequency axis, 20 Hz – Nyquist),
 * computed by an STFT in a Web Worker (falls back to the wasm renderer's
 * PNG), with overlays for the pitch track, spectral centroid and onsets, and
 * the playhead. Same zoom/scroll gestures and `view` sync as WaveformView.
 */
export function SpectrogramView({
  left,
  right,
  rate = 48000,
  view,
  onViewChange,
  playhead,
  onSeek,
  analysis,
  height = 180,
  className = '',
  floorDb = -90,
}: AudioViewProps & { floorDb?: number }) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const { width, dpr } = useElementSize(host);
  const duration = left ? left.length / rate : 0;
  const [v, setView] = useTimeView(duration, view, onViewChange);
  const gestures = useViewGestures(v, setView, duration, width, onSeek);
  const [stft, setStft] = useState<Stft | null>(null);
  const [png, setPng] = useState<ImageBitmap | null>(null);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const info = useMemo(() => normalizeAnalysis(analysis), [analysis]);

  useEffect(() => {
    let live = true;
    setStft(null);
    setPng(null);
    if (!left || !left.length) return;
    setBusy(true);
    void computeStft(left, right, rate).then(async (s) => {
      if (!live) return;
      if (s) setStft(s);
      else {
        const bytes = await wasmSpectrogram({ left, right: right ?? left, rate, warnings: [], analysis: null }, 1024, 256);
        if (bytes && live) {
          const bmp = await createImageBitmap(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' }));
          if (live) setPng(bmp);
        }
      }
      if (live) setBusy(false);
    });
    return () => {
      live = false;
    };
  }, [left, right, rate]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || !width) return;
    const W = Math.round(width * dpr);
    const H = Math.round(height * dpr);
    c.width = W;
    c.height = H;
    const g = c.getContext('2d');
    if (!g) return;
    g.fillStyle = '#000004';
    g.fillRect(0, 0, W, H);
    const span = v.end - v.start;
    const nyq = rate / 2;
    const yToF = (y: number) => F_MIN * Math.pow(nyq / F_MIN, 1 - y / H);
    const fToY = (f: number) => H * (1 - Math.log(Math.max(F_MIN, f) / F_MIN) / Math.log(nyq / F_MIN));

    if (stft) {
      const img = g.createImageData(W, H);
      const px = img.data;
      const lift = Math.round(((floorDb + 100) / 100) * 255);
      const scale = 255 / Math.max(1, 255 - lift);
      const rowBin = new Int32Array(H);
      for (let y = 0; y < H; y++) rowBin[y] = Math.min(stft.bins - 1, Math.round(yToF(y) / (rate / stft.fft)));
      for (let x = 0; x < W; x++) {
        const t = v.start + (x / W) * span;
        const col = Math.min(stft.cols - 1, Math.max(0, Math.round((t * rate) / stft.hop)));
        const base = col * stft.bins;
        for (let y = 0; y < H; y++) {
          const raw = stft.data[base + rowBin[y]!]!;
          const val = Math.max(0, Math.min(255, Math.round((raw - lift) * scale)));
          const o = (y * W + x) * 4;
          px[o] = PALETTE[val * 3]!;
          px[o + 1] = PALETTE[val * 3 + 1]!;
          px[o + 2] = PALETTE[val * 3 + 2]!;
          px[o + 3] = 255;
        }
      }
      g.putImageData(img, 0, 0);
    } else if (png && duration > 0) {
      const sx = (v.start / duration) * png.width;
      const sw = Math.max(1, (span / duration) * png.width);
      g.imageSmoothingEnabled = true;
      g.drawImage(png, sx, 0, sw, png.height, 0, 0, W, H);
    }

    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const w = width;
    const h = height;
    const xOf = (t: number) => ((t - v.start) / span) * w;
    // Frequency grid.
    g.font = '10px system-ui, sans-serif';
    for (const f of [100, 1000, 10000]) {
      if (f >= nyq) continue;
      const y = fToY(f) / dpr;
      g.strokeStyle = 'rgba(255,255,255,0.15)';
      g.beginPath();
      g.moveTo(0, y + 0.5);
      g.lineTo(w, y + 0.5);
      g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.6)';
      g.fillText(f >= 1000 ? `${f / 1000}k` : String(f), 3, y - 2);
    }
    const line = (pts: [number, number][], color: string, dash: number[]) => {
      if (!pts.length) return;
      g.strokeStyle = color;
      g.lineWidth = 1.5;
      g.setLineDash(dash);
      g.beginPath();
      let pen = false;
      let prevT = -Infinity;
      for (const [t, f] of pts) {
        if (t < v.start - span * 0.05 || t > v.end + span * 0.05 || !(f > 0)) {
          pen = false;
          continue;
        }
        const x = xOf(t);
        const y = fToY(f) / dpr;
        if (!pen || t - prevT > 0.1) g.moveTo(x, y);
        else g.lineTo(x, y);
        pen = true;
        prevT = t;
      }
      g.stroke();
      g.setLineDash([]);
      g.lineWidth = 1;
    };
    if (info?.pitchTrack.length) line(info.pitchTrack, '#5eead4', []);
    const centroid: [number, number][] = info?.centroidTrack.length
      ? info.centroidTrack
      : stft
        ? Array.from(stft.centroid, (f, i) => [(i * stft.hop) / rate, f] as [number, number]).filter((_, i) => i % Math.max(1, Math.floor(stft.cols / 1500)) === 0)
        : [];
    line(centroid, 'rgba(255,255,255,0.7)', [4, 3]);
    g.strokeStyle = 'rgba(251,191,36,0.85)';
    for (const t of info?.onsets ?? []) {
      if (t < v.start || t > v.end) continue;
      const x = Math.round(xOf(t)) + 0.5;
      g.beginPath();
      g.moveTo(x, 0);
      g.lineTo(x, 8);
      g.moveTo(x, h - 8);
      g.lineTo(x, h);
      g.stroke();
    }
  }, [stft, png, v.start, v.end, width, dpr, height, rate, info, duration, floorDb]);

  return (
    <div
      ref={host}
      className={`snd-view snd-spectrogram ${className}`}
      style={{ height }}
      {...gestures}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const t = v.start + ((e.clientX - r.left) / r.width) * (v.end - v.start);
        const f = F_MIN * Math.pow(rate / 2 / F_MIN, 1 - (e.clientY - r.top) / r.height);
        setHover(`${t.toFixed(3)} s · ${formatHz(f)}`);
      }}
      onMouseLeave={() => setHover(null)}
    >
      <canvas ref={canvas} style={{ width: '100%', height }} aria-label="Spectrogram" />
      <Playhead t={playhead} view={v} />
      <div className="snd-view-legend">
        {info?.pitchTrack.length ? <span className="is-pitch">pitch</span> : null}
        <span className="is-centroid">centroid</span>
        {info?.onsets.length ? <span className="is-onset">onsets</span> : null}
        {hover && <span className="mono">{hover}</span>}
      </div>
      {!left && <span className="snd-view-empty">Render to see the spectrogram.</span>}
      {left && busy && <span className="snd-view-empty">Analysing…</span>}
    </div>
  );
}
